import api from './api';

export interface ReportFilter {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'between' | 'in' | 'not_in';
  value: any;
}

export interface ReportConfiguration {
  name: string;
  description?: string;
  dataSource: 'members' | 'transactions' | 'classes' | 'bookings' | 'events' | 'custom';
  fields: string[];
  filters: ReportFilter[];
  groupBy?: string[];
  aggregations?: {
    field: string;
    operation: 'count' | 'sum' | 'avg' | 'min' | 'max';
  }[];
  sortBy?: {
    field: string;
    direction: 'ASC' | 'DESC';
  }[];
  chartType: 'bar' | 'line' | 'pie' | 'doughnut' | 'table' | 'card';
  dateRange?: {
    type: 'current' | 'custom';
    startDate?: Date;
    endDate?: Date;
    period?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  };
}

export interface SavedReport {
  id: string;
  name: string;
  description?: string;
  createdBy: string;
  configuration: ReportConfiguration;
  isShared: boolean;
  isTemplate: boolean;
  visibility: 'private' | 'shared' | 'public';
  createdAt: string;
  updatedAt: string;
}

class ReportAPI {
  // Dashboard overview
  async getDashboardOverview() {
    const response = await api.get('/reports/dashboard/overview');
    return response.data;
  }

  // Coaching report
  async getCoachingReport(params?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await api.get(
      `/reports/coaching?${queryParams.toString()}`
    );
    return response.data;
  }

  // Member statistics
  async getMemberStats() {
    const response = await api.get('/reports/members/stats');
    return response.data;
  }

  // Transaction statistics
  async getTransactionStats(params?: {
    startDate?: string;
    endDate?: string;
    type?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.type) queryParams.append('type', params.type);

    const response = await api.get(
      `/reports/transactions/stats?${queryParams.toString()}`
    );
    return response.data;
  }

  // Revenue report
  async getRevenueReport(params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month' | 'quarter';
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.groupBy) queryParams.append('groupBy', params.groupBy);

    const response = await api.get(
      `/reports/revenue?${queryParams.toString()}`
    );
    return response.data;
  }

  // Class statistics
  async getClassStats(params?: {
    startDate?: string;
    endDate?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get(
      `/api/reports/classes/stats?${queryParams.toString()}`
    );
    return response.data;
  }

  // Custom reports
  async createCustomReport(configuration: ReportConfiguration) {
    const response = await api.post(
      '/reports/custom',
      configuration
    );
    return response.data;
  }

  // Export report
  async exportReport(configuration: ReportConfiguration, format: 'csv' | 'json' = 'csv') {
    const response = await api.post(
      `/reports/export?format=${format}`,
      configuration,
      { 
        responseType: format === 'csv' ? 'blob' : 'json'
      }
    );
    return response.data;
  }

  // Saved reports
  async saveReport(report: {
    name: string;
    description?: string;
    configuration: ReportConfiguration;
    isShared?: boolean;
    visibility?: 'private' | 'shared' | 'public';
  }) {
    const response = await api.post(
      '/reports/save',
      report
    );
    return response.data;
  }

  async getSavedReports(): Promise<SavedReport[]> {
    const response = await api.get('/reports/saved');
    return response.data;
  }

  async executeReport(reportId: string) {
    const response = await api.get(
      `/reports/saved/${reportId}/execute`
    );
    return response.data;
  }

  async deleteReport(reportId: string) {
    await api.delete(`/reports/saved/${reportId}`);
  }

  // Report access tracking
  async logReportAccess(reportId: string) {
    try {
      const response = await api.post('/reports/log-access', { reportId });
      return response.data;
    } catch (err) {
      console.error('Error logging report access:', err);
      // Don't throw - this is non-critical
      return null;
    }
  }

  async getReportAccessHistory() {
    try {
      const response = await api.get('/reports/access-history');
      return response.data;
    } catch (err) {
      console.error('Error fetching report access history:', err);
      return { data: [] };
    }
  }

  // Helper method to download exported data
  downloadData(data: Blob | any, filename: string, format: 'csv' | 'json') {
    if (format === 'csv' && data instanceof Blob) {
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } else {
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }
}

export const reportAPI = new ReportAPI();
